import React, { useState, FormEvent } from 'react';

/**
 * SearchBar Component
 * 
 * Provides search functionality for jobs by jobId or filename.
 * 
 * Requirements: 3.1, 3.2, 3.3
 */

interface SearchBarProps {
  onSearch: (query: string) => void;
  resultsCount?: number;
  placeholder?: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  resultsCount,
  placeholder = 'Search by Job ID or filename...',
}) => {
  const [query, setQuery] = useState<string>('');

  /**
   * Handle search submission
   * Requirement 3.1: Provide search input field
   * Requirement 3.2: Search by jobId
   * Requirement 3.3: Search by filename
   */
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (query.trim()) {
      onSearch(query.trim());
    }
  };

  /**
   * Handle Enter key press
   */
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (query.trim()) {
        onSearch(query.trim());
      }
    }
  };

  /**
   * Handle clear search
   */
  const handleClear = () => {
    setQuery('');
    onSearch('');
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <form onSubmit={handleSubmit} className="flex flex-col gap-2">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={placeholder}
              className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {query && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
          <button
            type="submit"
            disabled={!query.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Search
          </button>
        </div>

        {/* Search results count - Requirement 3.3: Display search results count */}
        {resultsCount !== undefined && (
          <div className="text-sm text-gray-600">
            {resultsCount === 0 ? (
              <span className="text-orange-600">No results found</span>
            ) : resultsCount === 1 ? (
              <span>Found 1 result</span>
            ) : (
              <span>Found {resultsCount} results</span>
            )}
          </div>
        )}
      </form>
    </div>
  );
};

export default SearchBar;
